import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getLeagueSummary } from "../../../services/firebase/leagueService";
import {
  resolveLeagueWeekRecap,
  type LeagueWeekRecap,
} from "../utils/leagueRecap";
import { isSafeCelebrationRoute } from "../../gamification/hooks/useBadgeUnlockQueue";
import {
  isCelebrationBlocked,
  subscribeCelebrationGate,
} from "../../gamification/utils/celebrationGate";

const STORAGE_KEY = "lura.seenLeagueRecap";

/** ~600ms de assentamento — o mesmo do cartão de conquista, seu irmão. */
const SETTLE_MS = 600;

function storageKey(uid: string): string {
  return `${STORAGE_KEY}.${uid}`;
}

function hasSeen(uid: string, week: string): boolean {
  try {
    return window.localStorage.getItem(storageKey(uid)) === week;
  } catch {
    // Sem storage (modo privado), o recap reaparece na próxima carga do app.
    // É o lado seguro: repetir a celebração incomoda, engoli-la esconde o
    // resultado da semana inteira.
    return false;
  }
}

function markSeen(uid: string, week: string): void {
  try {
    window.localStorage.setItem(storageKey(uid), week);
  } catch {
    /* ignore */
  }
}

export interface UseLeagueWeekRecapResult {
  recap: LeagueWeekRecap | null;
  dismiss: () => void;
}

/**
 * Abre o recap da semana de liga — para TODO mundo que fechou uma sala, não só
 * para quem subiu.
 *
 * Substitui `useLeaguePromotionDetection`, que perguntava `isTierUp` e por isso
 * era mudo para quem ficou no meio ou caiu. Aqui a pergunta é outra: "a semana
 * fechou e eu ainda não vi o resultado?".
 *
 * ## Três coisas que o antecessor não fazia
 *
 * 1. **Gate de rota + assentamento.** O modal antigo abria o Share Studio em
 *    qualquer tela, inclusive no meio do onboarding ou de um checkout. Agora
 *    vale a mesma allowlist do cartão de conquista (`isSafeCelebrationRoute`) e
 *    o mesmo bloqueio compartilhado (`celebrationGate`), então duas celebrações
 *    automáticas nunca empilham.
 * 2. **Frescor.** Ver a guarda em `resolveLeagueWeekRecap`.
 * 3. **Claim por identidade de conta, nunca `cancelled`.** Um booleano de
 *    cancelamento com marca de "já pedi" se destrói sozinho no `StrictMode`: a
 *    2ª execução do efeito vê o pedido tomado e não pede nada, e a promessa da
 *    1ª é descartada — ninguém devolve o claim e o estado nunca assenta. Foi o
 *    BUG-141, e a cura registrada lá é exatamente esta.
 */
export function useLeagueWeekRecap(uid: string | null | undefined): UseLeagueWeekRecapResult {
  const { pathname } = useLocation();
  const [loaded, setLoaded] = useState<{ uid: string; recap: LeagueWeekRecap | null } | null>(null);
  const [blocked, setBlocked] = useState<boolean>(() => isCelebrationBlocked());
  const [open, setOpen] = useState<LeagueWeekRecap | null>(null);
  const requestedRef = useRef<string | null>(null);

  useEffect(() => subscribeCelebrationGate(setBlocked), []);

  useEffect(() => {
    if (!uid) {
      requestedRef.current = null;
      return;
    }
    if (requestedRef.current === uid) return;
    requestedRef.current = uid;

    void (async () => {
      try {
        const summary = await getLeagueSummary(uid);
        setLoaded({ uid, recap: resolveLeagueWeekRecap(summary) });
      } catch (err) {
        // Devolver o claim é o que permite uma segunda tentativa: sem isto uma
        // falha de rede na abertura do app esconderia o recap até o reload.
        requestedRef.current = null;
        console.error("[Lura] Falha ao carregar o recap da liga:", err);
      }
    })();
    // `uid` é a única dependência de propósito: o pedido é por CONTA, e trocar
    // de rota não pode refazê-lo.
  }, [uid]);

  // Derivado, não sincronizado por efeito: o recap de outra conta simplesmente
  // não é o desta, e não há estado a limpar.
  const recap = loaded && uid && loaded.uid === uid ? loaded.recap : null;

  const canOpen =
    !!recap &&
    !!uid &&
    !blocked &&
    isSafeCelebrationRoute(pathname) &&
    !hasSeen(uid, recap.week);

  useEffect(() => {
    if (open) return;
    if (!canOpen || !recap) return;
    const t = setTimeout(() => setOpen(recap), SETTLE_MS);
    return () => clearTimeout(t);
  }, [canOpen, recap, open]);

  const dismiss = () => {
    if (uid && open) markSeen(uid, open.week);
    setOpen(null);
  };

  return { recap: open, dismiss };
}
